import { html, nothing, type TemplateResult } from "lit";
import type { SessionsListResult } from "../types.ts";
import { resolveChatTabLabelWithHints, type ChatTabLabelHint } from "../chat/chat-tab-labels.ts";
import {
  collectSessionKeysForPicker,
  formatPathSegmentLabel,
  parseSessionKeyPath,
  pickSessionKeyForPathClick,
  SESSION_PATH_LEVEL_LABELS,
  uniquePathSegmentAtLevel,
  type SessionKeyPath,
} from "../chat/session-key-path.ts";

export type SessionPathRowActions = {
  agent?: TemplateResult | typeof nothing;
  kind?: TemplateResult | typeof nothing;
  id?: TemplateResult | typeof nothing;
};

export type SessionPathNavigatorProps = {
  activeSessionKey: string;
  activePath?: SessionKeyPath;
  openTabs: string[];
  sessions: SessionsListResult | null;
  hints?: Record<string, ChatTabLabelHint>;
  onSelectSession: (sessionKey: string) => void;
  rowActions?: SessionPathRowActions;
  showActiveFooter?: boolean;
  ariaLabel?: string;
  className?: string;
};

export function focusSessionPathRow(level: number, rootSelector = ".session-path-navigator") {
  requestAnimationFrame(() => {
    document
      .querySelector(`${rootSelector} .fleet-session-bar__row[data-path-level="${level}"]`)
      ?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
  });
}

/**
 * Compact ⋯ overflow for path-row actions. Keeps pills visible; actions open
 * only when the operator clicks the icon (same pattern as chat-history menus).
 */
export function renderRowActions(
  rowKey: string,
  buttons: TemplateResult | typeof nothing,
  ariaLabel = "Row actions",
) {
  if (buttons === nothing) {
    return nothing;
  }
  return html`
    <div class="fleet-session-bar__actions fleet-session-bar__actions--row">
      <details class="fleet-session-bar__row-menu" data-row-actions=${rowKey}>
        <summary
          class="fleet-session-bar__row-menu-btn"
          title=${ariaLabel}
          aria-label=${ariaLabel}
          @click=${(e: Event) => {
            e.stopPropagation();
            // Close sibling row menus so only one stays open.
            const current = e.currentTarget as HTMLElement;
            const root = current.closest(".session-path-navigator");
            root?.querySelectorAll("details.fleet-session-bar__row-menu[open]").forEach((el) => {
              if (el !== current.parentElement) {
                (el as HTMLDetailsElement).open = false;
              }
            });
          }}
        >
          ⋯
        </summary>
        <div
          class="fleet-session-bar__row-menu-panel"
          role="menu"
          @click=${(e: Event) => {
            e.stopPropagation();
            const details = (e.currentTarget as HTMLElement).closest("details");
            if (details) {
              details.open = false;
            }
          }}
        >
          ${buttons}
        </div>
      </details>
    </div>
  `;
}

export function renderPathRow(params: {
  level: number;
  activePath: SessionKeyPath;
  allKeys: string[];
  sessions: SessionsListResult | null;
  hints: Record<string, ChatTabLabelHint> | undefined;
  activeSessionKey: string;
  onSelectSession: (sessionKey: string) => void;
  trailingActions?: TemplateResult | typeof nothing;
  leafLabel?: string;
}) {
  const { level, activePath, allKeys } = params;
  const options = params.leafLabel ? [] : uniquePathSegmentAtLevel(allKeys, level, activePath);
  if (options.length === 0 && !params.leafLabel) {
    return nothing;
  }

  const label = SESSION_PATH_LEVEL_LABELS[level];

  return html`
    <div class="fleet-session-bar__row fleet-session-bar__row--path" data-path-level=${level}>
      <span class="fleet-session-path__level mono muted" title="Segment ${level + 1} of the session key">${label}</span>
      <div class="fleet-session-bar__scroll">
        ${
          params.leafLabel
            ? html`
              <div class="chat-session-tabs__tab chat-session-tabs__tab--active" role="presentation">
                <span class="chat-session-tabs__select fleet-session-path__leaf">
                  <span class="chat-session-tabs__label mono">${params.leafLabel}</span>
                </span>
              </div>
            `
            : options.map((value) => {
                const selected = (activePath[level] ?? "").trim() === value;
                const display = formatPathSegmentLabel(value, level);
                const matching = allKeys.filter((key) => {
                  const path = parseSessionKeyPath(key);
                  for (let i = 0; i < level; i++) {
                    if ((activePath[i] ?? "").trim() && path[i] !== activePath[i]) {
                      return false;
                    }
                  }
                  return path[level] === value;
                });
                const title =
                  matching.length === 1 ? matching[0]! : `${value} (${matching.length} sessions)`;
                return html`
                  <div
                    class="chat-session-tabs__tab ${selected ? "chat-session-tabs__tab--active" : ""}"
                    role="presentation"
                  >
                    <button
                      type="button"
                      class="chat-session-tabs__select"
                      title=${title}
                      aria-label=${`${label}: ${display}${selected ? ", selected" : ""}`}
                      @click=${() => {
                        const nextKey = pickSessionKeyForPathClick({
                          keys: allKeys,
                          activeSessionKey: params.activeSessionKey,
                          prefix: activePath,
                          level,
                          value,
                          sessions: params.sessions,
                        });
                        if (nextKey) {
                          params.onSelectSession(nextKey);
                        }
                      }}
                    >
                      <span class="chat-session-tabs__label">${display}</span>
                    </button>
                  </div>
                `;
              })
        }
      </div>
      ${params.trailingActions ?? nothing}
    </div>
  `;
}

export function renderSessionPathNavigator(props: SessionPathNavigatorProps) {
  const activeKey = props.activeSessionKey.trim();
  const activePath = props.activePath ?? parseSessionKeyPath(activeKey);
  const allKeys = collectSessionKeysForPicker({
    activeSessionKey: activeKey,
    openTabs: props.openTabs,
    sessions: props.sessions,
  });
  const hints = props.hints;
  const rowActions = props.rowActions;
  const idRowActions = rowActions?.id ?? nothing;
  const hasIdRowActions = idRowActions !== nothing;

  const row4Options = uniquePathSegmentAtLevel(allKeys, 3, activePath);
  const row4Leaf =
    row4Options.length === 0 && activePath[3]
      ? formatPathSegmentLabel(activePath[3], 3)
      : undefined;

  const pathClass = props.className ?? "fleet-session-path";
  const ariaLabel = props.ariaLabel ?? "Session key navigator";

  return html`
    <div
      class="session-path-navigator ${pathClass}"
      role="region"
      aria-label=${ariaLabel}
    >
      ${renderPathRow({
        level: 0,
        activePath,
        allKeys,
        sessions: props.sessions,
        hints,
        activeSessionKey: activeKey,
        onSelectSession: props.onSelectSession,
      })}
      ${renderPathRow({
        level: 1,
        activePath,
        allKeys,
        sessions: props.sessions,
        hints,
        activeSessionKey: activeKey,
        onSelectSession: props.onSelectSession,
        trailingActions: rowActions?.agent,
      })}
      ${renderPathRow({
        level: 2,
        activePath,
        allKeys,
        sessions: props.sessions,
        hints,
        activeSessionKey: activeKey,
        onSelectSession: props.onSelectSession,
        trailingActions: rowActions?.kind,
      })}
      ${
        row4Options.length > 0
          ? renderPathRow({
              level: 3,
              activePath,
              allKeys,
              sessions: props.sessions,
              hints,
              activeSessionKey: activeKey,
              onSelectSession: props.onSelectSession,
              trailingActions: idRowActions,
            })
          : row4Leaf
            ? renderPathRow({
                level: 3,
                activePath,
                allKeys,
                sessions: props.sessions,
                hints,
                activeSessionKey: activeKey,
                onSelectSession: props.onSelectSession,
                leafLabel: row4Leaf,
                trailingActions: idRowActions,
              })
            : hasIdRowActions
              ? renderPathRow({
                  level: 3,
                  activePath,
                  allKeys,
                  sessions: props.sessions,
                  hints,
                  activeSessionKey: activeKey,
                  onSelectSession: props.onSelectSession,
                  leafLabel: "—",
                  trailingActions: idRowActions,
                })
              : nothing
      }
      ${
        props.showActiveFooter !== false
          ? html`
              <p class="fleet-session-path__active muted" title=${activeKey}>
                Active:
                <span class="mono">${resolveChatTabLabelWithHints(activeKey, 0, props.sessions, hints)}</span>
              </p>
            `
          : nothing
      }
    </div>
  `;
}

export const focusPathRow = focusSessionPathRow;
